import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setIsAdvancedSpacingModelOpen } from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';
import { useCloseAdvancedSpacingOnDeselect } from './useCloseAdvancedSpacingOnDeselect';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { useNewBlockStyleSettings } from '../../../../hooks/useNewBlockSettings';
import { SecondaryMenu, SecondaryMenuItemTitle, SecondaryMenuSectionTitle } from '../../../component';
import { trimLeadingZeros } from '../../../../helpers/trimLeadingZeros';

const PaddingInput = ({
  label,
  name,
  value,
  onFocus,
  onCommit,
}: {
  label: string;
  name: string;
  value: number | undefined;
  onFocus: () => void;
  onCommit: (name: string, value: number) => void;
}) => {
  const [localValue, setLocalValue] = useState(value?.toString() ?? '');
  const localValueRef = useRef(localValue);
  const valueRef = useRef(value);
  const onCommitRef = useRef(onCommit);

  useEffect(() => {
    setLocalValue(value?.toString() ?? '');
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleBlur = () => {
    const trimmed = trimLeadingZeros(localValue);
    const num = parseFloat(trimmed) || 0;
    const finalNum = Math.max(0, num);
    setLocalValue(finalNum.toString());
    onCommit(name, finalNum);
  };

  // Safety net: if the component unmounts (e.g., user clicks away to another block)
  // before blurring, commit the buffered value to Redux.
  useEffect(() => {
    return () => {
      const trimmed = trimLeadingZeros(localValueRef.current);
      const num = parseFloat(trimmed) || 0;
      const finalNum = Math.max(0, num);
      if (finalNum !== (valueRef.current ?? 0)) {
        onCommitRef.current(name, finalNum);
      }
    };
  }, [name]);

  return (
    <Grid item xs={6}>
      <TextField
        label={label}
        name={name}
        value={localValue}
        onFocus={onFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
        }}
        type="number"
        color="secondary"
        InputProps={{ inputProps: { min: 0 } }}
        variant="outlined"
        fullWidth
        sx={{
          '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'reset', // Hide the up and down arrows
            margin: 0,
          },
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
    </Grid>
  );
};

export function AdvancedSpacingWithUndoRedo() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const { getBlockStyle, saveCurrentStateToRef, updateBlockStyleWithUndo } = useNewBlockStyleSettings(blockId);

  const { paddingTop = 0, paddingBottom = 0, paddingLeft = 0, paddingRight = 0 } = getBlockStyle(blockId);

  useCloseAdvancedSpacingOnDeselect(selectedBlockIdByWrapper);

  const handleFocus = useCallback(() => {
    saveCurrentStateToRef(getBlockStyle(blockId));
  }, [blockId, getBlockStyle, saveCurrentStateToRef]);

  const handleCommit = useCallback(
    (name: string, value: number) => {
      updateBlockStyleWithUndo({ [name]: value });
    },
    [updateBlockStyleWithUndo]
  );

  const onBackButtonOnClick = useCallback(() => {
    dispatch(setIsAdvancedSpacingModelOpen(false));
  }, [dispatch]);

  return (
    <SecondaryMenu
      menuTitle={t('document.grid.text.advanced_spacing.title')}
      backButtonOnClick={onBackButtonOnClick}
      testId={'advanced-spacing-component'}
    >
      <Stack rowGap="16px" width="100%">
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.text.advanced_spacing.spacing')}</SecondaryMenuSectionTitle>
        <div>
          <SecondaryMenuItemTitle>{t('document.grid.text.advanced_spacing.padding')}</SecondaryMenuItemTitle>
          <Box sx={{ justifyContent: 'space-between', marginTop: '16px' }}>
            <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
              {/* We need the key here to force unmount when switching blocks.
                  This triggers the cleanup effect in PaddingInput to save buffered state. */}
              <PaddingInput
                key={`paddingTop-${blockId}`}
                label="Top"
                name="paddingTop"
                value={paddingTop}
                onFocus={handleFocus}
                onCommit={handleCommit}
              />

              <PaddingInput
                key={`paddingBottom-${blockId}`}
                label="Bottom"
                name="paddingBottom"
                value={paddingBottom}
                onFocus={handleFocus}
                onCommit={handleCommit}
              />

              <PaddingInput
                key={`paddingLeft-${blockId}`}
                label="Left"
                name="paddingLeft"
                value={paddingLeft}
                onFocus={handleFocus}
                onCommit={handleCommit}
              />

              <PaddingInput
                key={`paddingRight-${blockId}`}
                label="Right"
                name="paddingRight"
                value={paddingRight}
                onFocus={handleFocus}
                onCommit={handleCommit}
              />
            </Grid>
          </Box>
        </div>
      </Stack>
    </SecondaryMenu>
  );
}
